/**
 * Mirrors WPF `NavigationVisibility`: tenant feature gate AND role permission gate.
 * Administration features bypass the tenant plan and stay role-only.
 */

export type PermissionLevel = 'None' | 'Read' | 'Write' | 'Reports' | 'Admin';

export interface PermissionSnapshot {
  canRead: boolean;
  canWrite: boolean;
  canReports: boolean;
  canAdmin: boolean;
}

/** `Features.Administration` — EntitlementsAdmin and TenantUserAdmin. */
export const ADMINISTRATION_FEATURES = new Set(['EntitlementsAdmin', 'TenantUserAdmin']);

export function isAdministrationFeature(feature: string | undefined): boolean {
  return feature != null && ADMINISTRATION_FEATURES.has(feature);
}

/**
 * Tenant entitlement gate. Null `enabledFeatures` means no server filter (fail-open).
 * Empty / missing feature is always visible at this gate.
 */
export function isFeatureVisible(
  feature: string | undefined,
  enabledFeatures: ReadonlySet<string> | null
): boolean {
  if (isAdministrationFeature(feature)) return true;
  if (enabledFeatures == null) return true;
  if (!feature) return true;
  return enabledFeatures.has(feature);
}

/** Role gate. Mirrors WPF `NavigationVisibility.HasPermission`. */
export function hasPermission(level: PermissionLevel, snapshot: PermissionSnapshot): boolean {
  switch (level) {
    case 'None':
      return true;
    case 'Read':
      return snapshot.canRead;
    case 'Write':
      return snapshot.canWrite;
    case 'Reports':
      return snapshot.canReports;
    case 'Admin':
      return snapshot.canAdmin;
    default:
      return true;
  }
}

export function isNavigationVisible(
  feature: string | undefined,
  permission: PermissionLevel,
  enabledFeatures: ReadonlySet<string> | null,
  snapshot: PermissionSnapshot
): boolean {
  return isFeatureVisible(feature, enabledFeatures) && hasPermission(permission, snapshot);
}
